// components/AddDebtModal.jsx
import { useState } from 'react';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import { showToast } from './Toast';

const AddDebtModal = ({ debt, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: debt?.name || '',
    type: debt?.type || 'credit_card',
    original_balance: debt?.original_balance || '',
    current_balance: debt?.current_balance || '',
    apr: debt?.apr || '',
    minimum_payment: debt?.minimum_payment || '',
    next_due_date: debt?.next_due_date || '',
    creditor: debt?.creditor || '',
    account_number_last4: debt?.account_number_last4 || '',
    credit_limit: debt?.credit_limit || '',
    is_autopay: debt?.is_autopay || true,
    notes: debt?.notes || '',
    // Interest calculation fields (credit cards)
    interest_calculation_method: debt?.interest_calculation_method || 'ADB',
    statement_closing_day: debt?.statement_closing_day || '',
    // Mortgage-specific fields
    payment_frequency: debt?.payment_frequency || 'monthly',
    biweekly_payment: debt?.biweekly_payment || '',
    escrow_balance: debt?.escrow_balance || ''
  });
  
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        original_balance: formData.original_balance ? parseFloat(formData.original_balance) : 0,
        current_balance: parseFloat(formData.current_balance),
        apr: parseFloat(formData.apr),
        minimum_payment: parseFloat(formData.minimum_payment),
        payment_day: formData.payment_day ? parseInt(formData.payment_day) : null,
        credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null,
        // Interest calculation fields
        statement_closing_day: formData.statement_closing_day ? parseInt(formData.statement_closing_day) : null,
        // Mortgage-specific
        biweekly_payment: formData.biweekly_payment ? parseFloat(formData.biweekly_payment) : null,
        escrow_balance: formData.escrow_balance ? parseFloat(formData.escrow_balance) : 0
      };

      if (debt) {
        await axios.put(`${API_BASE_PATH}/debts/${debt.id}`, payload);
        showToast('Debt updated successfully', 'success');
      } else {
        await axios.post(`${API_BASE_PATH}/debts`, payload);
        showToast('Debt added successfully', 'success');
      }
      
      onSave();
    } catch (error) {
      console.error('Error saving debt:', error);
      showToast('Failed to save debt', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isCreditCard = formData.type === 'credit_card';
  const isMortgage = formData.type === 'mortgage';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            <i className="fas fa-credit-card"></i>
            {debt ? 'Edit Debt' : 'Add New Debt'}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Debt Name *</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Chase Freedom Card, Toyota Camry Loan"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Type *</label>
                <select
                  name="type"
                  className="form-control"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="credit_card">Credit Card</option>
                  <option value="mortgage">Mortgage</option>
                  <option value="auto_loan">Auto Loan</option>
                  <option value="personal_loan">Personal Loan</option>
                  <option value="student_loan">Student Loan</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Creditor/Lender</label>
                <input
                  type="text"
                  name="creditor"
                  className="form-control"
                  value={formData.creditor}
                  onChange={handleChange}
                  placeholder="Bank or lender name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Original Balance {!isCreditCard && '*'}
                </label>
                <input
                  type="number"
                  name="original_balance"
                  className="form-control"
                  value={formData.original_balance}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="0.00"
                  required={!isCreditCard}
                />
                {isCreditCard && (
                  <small style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                    Optional for credit cards
                  </small>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Current Balance *</label>
                <input
                  type="number"
                  name="current_balance"
                  className="form-control"
                  value={formData.current_balance}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">APR % *</label>
                <input
                  type="number"
                  name="apr"
                  className="form-control"
                  value={formData.apr}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  {isMortgage ? 'Monthly Payment *' : 'Minimum Payment *'}
                </label>
                <input
                  type="number"
                  name="minimum_payment"
                  className="form-control"
                  value={formData.minimum_payment}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="0.00"
                  required
                />
                {isMortgage && (
                  <small style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                    Total monthly payment (principal + interest + escrow)
                  </small>
                )}
              </div>

              {isMortgage && (
                <>
                  <div className="form-group">
                    <label className="form-label">Payment Frequency *</label>
                    <select
                      name="payment_frequency"
                      className="form-control"
                      value={formData.payment_frequency}
                      onChange={handleChange}
                      required
                    >
                      <option value="monthly">Monthly</option>
                      <option value="biweekly">Bi-weekly</option>
                    </select>
                  </div>

                  {formData.payment_frequency === 'biweekly' && (
                    <div className="form-group">
                      <label className="form-label">Bi-weekly Payment *</label>
                      <input
                        type="number"
                        name="biweekly_payment"
                        className="form-control"
                        value={formData.biweekly_payment}
                        onChange={handleChange}
                        step="0.01"
                        placeholder="0.00"
                        required
                      />
                      <small style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                        Amount paid every 2 weeks
                      </small>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Escrow Balance</label>
                    <input
                      type="number"
                      name="escrow_balance"
                      className="form-control"
                      value={formData.escrow_balance}
                      onChange={handleChange}
                      step="0.01"
                      placeholder="0.00"
                    />
                    <small style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                      Current amount in escrow (taxes, insurance)
                    </small>
                  </div>
                </>
              )}

              {isCreditCard && (
                <>
                  <div className="form-group">
                    <label className="form-label">Credit Limit</label>
                    <input
                      type="number"
                      name="credit_limit"
                      className="form-control"
                      value={formData.credit_limit}
                      onChange={handleChange}
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Interest Calculation Method</label>
                    <select
                      name="interest_calculation_method"
                      className="form-control"
                      value={formData.interest_calculation_method}
                      onChange={handleChange}
                    >
                      <option value="ADB">Average Daily Balance (ADB)</option>
                      <option value="BSIR">Balance Subject to Interest Rate (BSIR)</option>
                    </select>
                    <small style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                      Used for auto-calculating interest charges
                    </small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Statement Closing Day</label>
                    <input
                      type="number"
                      name="statement_closing_day"
                      className="form-control"
                      value={formData.statement_closing_day}
                      onChange={handleChange}
                      min="1"
                      max="31"
                      placeholder="e.g., 15"
                    />
                    <small style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                      Day of month (1-31) when statement closes
                    </small>
                  </div>
                </>
              )}

              <div className="form-group">
                <label className="form-label">Next Due Date</label>
                <input
                  type="date"
                  name="next_due_date"
                  className="form-control"
                  value={formData.next_due_date}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                  type="checkbox"
                  name="is_autopay"
                  checked={formData.is_autopay}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_autopay: e.target.checked }))}
                  />
                  <span className="form-label" style={{ margin: 0 }}>
                  AutoPay Enabled
                  <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '6px' }}>
                      (Payment automatically withdrawn from account)
                  </span>
                  </span>
              </label>
              </div>

              <div className="form-group">
                <label className="form-label">Account Last 4 Digits</label>
                <input
                  type="text"
                  name="account_number_last4"
                  className="form-control"
                  value={formData.account_number_last4}
                  onChange={handleChange}
                  maxLength="4"
                  placeholder="1234"
                />
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Notes</label>
                <textarea
                  name="notes"
                  className="form-control"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Any additional notes..."
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  {debt ? 'Update Debt' : 'Add Debt'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDebtModal;